import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { org } from 'src/backend';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useAuth from './useAuth';

// ----------------------------------------------------------------------

/**
 * @typedef UseBusiness
 * @property {Business[]} business
 * @property {boolean} isLoading
 */

/**
 * Encapsulates business data state management & logic, returning that logic's
 * state.
 * @returns {UseBusiness}
 */
export default function useBusiness({
  history,
  user,
  isNonProfit,
  allOrgs,
  fetchOnMount = true,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('translation');
  const {
    user: { is_executive },
  } = useAuth();

  const [cachedOrgs, setCachedOrgs] = useState(null);
  const [orgsInCountry, setOrgsInCountry] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState({});
  const [matchingOrgLoading, setMatchingOrgLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [NPOTypes, setNPOTypes] = useState([]);
  const [postOrgLoading, setPostOrgLoading] = useState(false);
  const [updateOrgLoading, setUpdateOrgLoading] = useState(false);
  const [nearbyNPOs, setNearbyNPOs] = useState([]);
  const [nearbyNPOsLoading, setNearbyNPOsLoading] = useState(false);
  const [hasLoadedNearbyNPOs, setHasLoadedNearbyNPOs] = useState(false);
  const [nearbyDonors, setNearbyDonors] = useState([]);

  useEffect(() => {
    if (user && user.province && fetchOnMount) {
      let isMounted = true;

      setIsLoading(true);
      org
        .getOrgsByProvince({
          province: user.province,
          isNonProfit,
        })
        .then((business) => {
          if (isMounted) {
            setCachedOrgs(business);
            setIsLoading(false);
          }
        })
        .catch(() => {
          if (isMounted) {
            setIsLoading(false);
            enqueueSnackbar(
              'An error occurred while trying to fetch the businesses. Please try again later.',
              { variant: 'error' }
            );
          }
        });
      org
        .getOrgsByCountry({
          country: user.country,
          isNonProfit,
          allOrgs,
        })
        .then((business) => {
          if (isMounted) {
            setOrgsInCountry(business);
          }
        })
        .catch(() => {
          if (isMounted) {
            enqueueSnackbar(
              'An error occurred while trying to fetch the businesses. Please try again later.',
              { variant: 'error' }
            );
          }
        });

      return () => {
        isMounted = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, user, isNonProfit]);

  const searchExistingOrgs = async (data) => {
    setMatchingOrgLoading(true);
    try {
      const response = await org.searchExistingOrgs(data);

      if (response.isBusinessAvailable) {
        const orgsArray = response.business;

        if (!is_executive) {
          const existing = orgsArray[0];
          enqueueSnackbar(
            existing?.pending_approval
              ? t('organization.already-exists-pending-approval')
              : t('organization.already-exists-at-location'),
            { variant: 'warning' }
          );
          history.push(PATH_DASHBOARD.viewOrg(existing?.team_id));
          return orgsArray;
        }

        return orgsArray;
      }

      return [];
    } catch (error) {
      enqueueSnackbar(
        'An error occurred while trying to fetch the businesses. Please try again later.',
        { variant: 'error' }
      );
      return [];
    } finally {
      setMatchingOrgLoading(false);
    }
  };

  const postOrgData = async (values) => {
    setPostOrgLoading(true);
    return org
      .postOrg(values)
      .then((data) => {
        setPostOrgLoading(false);
        enqueueSnackbar('Organization is created successfully', {
          variant: 'success',
        });
        if (values?.admin) {
          enqueueSnackbar(`Email sent to ${values.admin.email}`, {
            variant: 'success',
          });
        }
        if (is_executive) {
          history.push(PATH_DASHBOARD.executive.viewOrg(data.id));
        } else {
          history.push(PATH_DASHBOARD.viewOrg(data.team_id));
        }
      })
      .catch(() => {
        setPostOrgLoading(false);
        enqueueSnackbar(
          'An error occurred while trying to submit the business data. Please try again later.',
          { variant: 'error' }
        );
      });
  };

  const updateOrgData = (values) => {
    setUpdateOrgLoading(true);
    org
      .updateBusiness(values)
      .then(() => {
        setUpdateOrgLoading(false);
        enqueueSnackbar('Business is updated successfully', {
          variant: 'success',
        });
        if (history) {
          history.push(
            isNonProfit ? PATH_DASHBOARD.npo : PATH_DASHBOARD.business
          );
        }
      })
      .catch(() => {
        setUpdateOrgLoading(false);
        enqueueSnackbar(
          'An error occurred while trying to submit the business data. Please try again later.',
          { variant: 'error' }
        );
      });
  };

  useEffect(() => {
    if (fetchOnMount) {
      if (isNonProfit) {
        org
          .getNPOTypes()
          .then((data) => {
            setNPOTypes(data.NPOTypes);
          })
          .catch(() => {
            enqueueSnackbar(
              'An error occurred while trying to fetch the NPO types. Please try again later.',
              { variant: 'error' }
            );
          });
      } else {
        org
          .getBusinessTypes()
          .then((data) => {
            setBusinessTypes(data.businessTypes);
          })
          .catch(() => {
            enqueueSnackbar(
              'An error occurred while trying to fetch the business types. Please try again later.',
              { variant: 'error' }
            );
          });
      }
    }
  }, [enqueueSnackbar, isNonProfit]);

  const getOrgById = ({ id }) => {
    org
      .getOrgById({ id })
      .then((data) => setSelectedOrg(data))
      .catch(() => {
        enqueueSnackbar(
          'An error occurred while trying to fetch the organization. Please try again later.',
          { variant: 'error' }
        );
      });
  };

  const getNearByNPOs = ({
    lat,
    lng,
    donationLat = null,
    donationLng = null,
    includeSelf = false,
    includeOnboarding = false,
    isDonationCreation = true,
    ignoreTeamId = null,
    otherTeamIdToCheck = null,
    includeTeamIds = [],
  }) => {
    setNearbyNPOsLoading(true);
    org
      .getNearByNPOs({
        lat,
        lng,
        donationLat,
        donationLng,
        includeSelf,
        includeOnboarding,
        isDonationCreation,
        ignoreTeamId,
        otherTeamIdToCheck,
        includeTeamIds,
        skipPagination: true,
      })
      .then(({ orgs }) => {
        setNearbyNPOs(orgs);
        setHasLoadedNearbyNPOs(true);
      })
      .catch(() => {
        enqueueSnackbar(
          'An error occurred while trying to fetch the nearby organizations. Please try again later.',
          { variant: 'error' }
        );
      })
      .finally(() => {
        setNearbyNPOsLoading(false);
      });
  };

  const getNearByDonors = ({ lat, lng }) => {
    org
      .getNearByDonors({ lat, lng, skipPagination: true })
      .then(({ orgs }) => {
        setNearbyDonors(orgs);
      })
      .catch(() => {
        enqueueSnackbar(
          'An error occurred while trying to fetch the nearby organizations. Please try again later.',
          { variant: 'error' }
        );
      });
  };

  return {
    orgs: cachedOrgs ?? [],
    searchExistingOrgs,
    isLoading,
    matchingOrgLoading,
    businessTypes,
    postOrgData,
    postOrgLoading,
    updateOrgData,
    updateOrgLoading,
    getOrgById,
    selectedOrg,
    NPOTypes,
    getNearByNPOs,
    nearbyNPOs,
    nearbyNPOsLoading,
    hasLoadedNearbyNPOs,
    getNearByDonors,
    nearbyDonors,
    orgsInCountry,
  };
}
